"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useState } from "react";

const CLOCK_UPDATE_INTERVAL_MS = 1000;
const SECONDS_PER_MINUTE = 60;

function getElapsedSeconds(
  elapsedBeforeCurrentSegmentMs: number,
  startedAtMs: number,
): number {
  return Math.max(
    0,
    Math.floor(
      (elapsedBeforeCurrentSegmentMs + Date.now() - startedAtMs) / 1000,
    ),
  );
}

/** Activity の秒数を短く読みやすい分・秒表記へ変換する。 */
export function formatActivityDuration(durationSeconds: number): string {
  const minutes = Math.floor(durationSeconds / SECONDS_PER_MINUTE);
  const seconds = durationSeconds % SECONDS_PER_MINUTE;

  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/** Activity 全体を再描画せず、実行中の経過時間だけを毎秒更新する。 */
export function ActivityWorkingTime({
  elapsedBeforeCurrentSegmentMs,
  startedAtMs,
}: {
  elapsedBeforeCurrentSegmentMs: number;
  startedAtMs: number;
}) {
  const { i18n } = useLingui();
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    getElapsedSeconds(elapsedBeforeCurrentSegmentMs, startedAtMs),
  );
  const workingLabel = i18n._(msg`Working`);
  const formattedDuration = formatActivityDuration(elapsedSeconds);

  useEffect(() => {
    const updateElapsedSeconds = () => {
      setElapsedSeconds(
        getElapsedSeconds(elapsedBeforeCurrentSegmentMs, startedAtMs),
      );
    };
    updateElapsedSeconds();

    const interval = window.setInterval(
      updateElapsedSeconds,
      CLOCK_UPDATE_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, [elapsedBeforeCurrentSegmentMs, startedAtMs]);

  return (
    <div
      aria-label={`${formattedDuration} ${workingLabel}`}
      className="flex w-full max-w-[60rem] items-center gap-1.5 border-border/60 border-b pb-2 text-[13px] text-muted-foreground leading-5"
      data-activity-working-time=""
    >
      <span className="font-medium text-foreground/70 tabular-nums">
        {formattedDuration}
      </span>
      <span>{workingLabel}</span>
    </div>
  );
}
